'use client'

import { type TextSize, themeIconStrokeColorMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { Text } from '../text/text.tsx'

type ProgressCircleSize = 'med'

type CircleSizeConfig = {
  width: string
  height: string
  strokeWidth: number
  fontSize: TextSize
}

type ProgressCircleProps = {
  className?: string
  completed: number
  total: number
  size?: ProgressCircleSize
}

const sizeMap: Record<ProgressCircleSize, CircleSizeConfig> = {
  med: {
    width: 'w-16',
    height: 'h-16',
    strokeWidth: 5,
    fontSize: 'base',
  },
} as const

export const ProgressCircle = ({
  className,
  completed,
  total,
  size = 'med',
}: ProgressCircleProps) => {
  const { width, height, strokeWidth, fontSize } = sizeMap[size]

  const percent = Math.floor((completed / total) * 100)

  const center = 50
  const radius = center - strokeWidth / 2
  const circumference = 2 * Math.PI * radius

  const strokeDashoffset = circumference - (percent / 100) * circumference

  return (
    <div className={cn('relative', className)}>
      <svg
        className={cn('transform -rotate-90', width, height)}
        viewBox="-7 -7 114 114"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={`Progress Circle ${percent}%`}
        role="img"
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          className={themeIconStrokeColorMap.disabled}
          strokeWidth={strokeWidth}
        />

        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          className={cn(
            'transition-all duration-300 ease-in-out drop-shadow-glow-blue-light',
            themeIconStrokeColorMap.info
          )}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        <Text fontSize={fontSize} fontWeight="medium">
          {completed}/{total}
        </Text>
      </div>
    </div>
  )
}
